// Adelphi University: public college page (#2746), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Adelphi's newest posted Common Data Set is 2022-2023 (Institutional
//    Research, Data page), three cycles old, so it is not used. The
//    first-year counts come from the U.S. Department of Education's IPEDS
//    Admissions survey for Fall 2024, first-time degree-seeking students
//    (College Navigator and the ADM2024 complete data file, unit 188429).
//  - Adelphi Admissions, How to Apply as a First-Year Student: the Important
//    Dates table, the Early Action note, test policy, Nursing direct-admission
//    standard, auditions and portfolios, and the early assurance programs.
//    The page names no entry year; it is the page as posted for the
//    application open now (Fall 2027 entry).
//  - Adelphi One Stop, Estimated Cost of Attendance for Undergraduate
//    Students, the 2026-2027 full-time budget for a resident student.
//  - College Scorecard, school id 188429 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=188429#admsns',
        read: '2026-10-01',
    },
    apply: {
        label: 'Adelphi Admissions, How to Apply as a First-Year Student',
        url: 'https://www.adelphi.edu/admissions/first-year/apply/',
        read: '2026-10-01',
    },
    costs: {
        label: 'Adelphi One Stop, Estimated Cost of Attendance for Undergraduate Students',
        url: 'https://www.adelphi.edu/one-stop/educational-cost/estimated-cost-of-attendance/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?188429-Adelphi-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'adelphi-university',
    kbId: 'adelphi_university',
    name: 'Adelphi University',
    shortName: 'Adelphi',
    location: 'Garden City, NY',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Adelphi brand colors page); decoration only.
    branding: {
        primary: '#FFB500',
        secondary: '#4F2C1D',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/adelphi_university_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 19705,
        admitted: 12987,
        enrolled: 1343,
        source: 'ipeds',
        where: 'first-time, degree-seeking first-year students',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Adelphi has not posted a Common Data Set since 2022-2023, so these counts come from the Department of Education\'s IPEDS survey for Fall 2024, which gives one first-year rate for the whole university and none by school or major.',
        source: 'ipeds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'Adelphi has no early decision plan. Early action is not binding: apply by November 1 to be among the first considered for scholarships and need-based aid, and if every document is in by then, Adelphi sends a decision by December 31. Adelphi does not publish early action counts.',
        source: 'apply',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Adelphi through the Common App. Some programs ask for more:',
        rows: [
            { school: 'Nursing', requirement: 'For direct admission as a Nursing major you need at least one high school biology or chemistry course (with a lab) and a cumulative GPA of 3.20 or higher, with strong grades in math and science.' },
            { school: 'Performing and visual arts', requirement: 'Studio art, art and design education, dance, music, music education and theatre applicants must complete a portfolio review or an audition with the department before Adelphi can make a decision.' },
            { school: 'Early assurance programs', requirement: 'Joint programs with partner schools (optometry, osteopathic medicine, dentistry, law, podiatric medicine) have their own deadlines from January 1 to April 1, a minimum weighted GPA, and, for all but the LECOM Medical 4+4, a minimum SAT or ACT score from one sitting. Adelphi encourages these applicants to apply by November 1.' },
        ],
        source: 'apply',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Priority deadline', value: 'March 1' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'From November 15', binding: false },
            { plan: 'Rolling Admission', deadline: 'March 1 (priority)', decision: 'From November 15', binding: false },
        ],
        note: 'After decisions begin on November 15, Adelphi sends a decision within two to four weeks of a complete application. Applications after March 1 are considered as space allows, and so are deposits after May 1. The application fee is $50, and Adelphi asks you to contact admissions if the fee is a barrier.',
        source: 'apply',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Adelphi is test-optional: you choose whether to send SAT or ACT scores, and you are considered for its merit scholarships either way. Some early assurance programs require scores because their partner schools do.',
        source: 'apply',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition and fees', amount: 54050 },
            { item: 'Books and supplies', amount: 870 },
            { item: 'Food and housing (on campus)', amount: 20680 },
            { item: 'Transportation (estimated)', amount: 840 },
            { item: 'Personal expenses (estimated)', amount: 3300 },
            { item: 'Loan origination fees', amount: 70 },
        ],
        total: 79810,
        note: 'The full-time budget for a student living on campus, as Adelphi lists it, including its estimate of federal loan fees.',
        source: 'costs',
        netPrice: {
            average: 26646,
            byIncome: [
                { income: '$0 to $30,000', amount: 22263 },
                { income: '$30,001 to $48,000', amount: 21710 },
                { income: '$48,001 to $75,000', amount: 23891 },
                { income: '$75,001 to $110,000', amount: 27224 },
                { income: 'Over $110,000', amount: 32262 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Adelphi\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
